/*
  useGraphSettings.js
*/

// Npm.
import { useState, useCallback, useMemo } from 'react';

// Lib.
import getDefaultSettings from '../../utils/getDefaultSettings';
import localStorageJSON from '../../utils/localStorageJSON';

// Constants.
const STORAGE_KEY = 'botech_graph_settings';

/**
 * Hook to get/set the current graph settings.
 */
export default function useGraphSettings(forceTheme = null) {

  // Get stored value with defaults.
  const [value, setValue] = useState(() => ({
    ...getDefaultSettings(),
    ...localStorageJSON.getItem(STORAGE_KEY),
  }));

  // Callback that supports value or function as an argument,
  // similar to useState behaviour.
  const handleSetValue = useCallback((valueOrFunc) => {
    if (typeof valueOrFunc === 'function') {
      setValue((v) => {
        const next = valueOrFunc(v);
        localStorageJSON.setItem(STORAGE_KEY, next);
        return next;
      });
    } else {
      localStorageJSON.setItem(STORAGE_KEY, valueOrFunc);
      setValue(valueOrFunc);
    }
  }, [setValue]);

  // Ensure the theme is enforced.
  const useValue = useMemo(() => {
    if (forceTheme === 'light' && value.isDark) {
      return { ...value, isDark: false };
    } else if (forceTheme === 'dark' && !value.isDark) {
      return { ...value, isDark: true };
    }
    return value;
  }, [value, forceTheme]);

  return [useValue, handleSetValue];

}
